import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PaperDraggable from "../../../../../components/PaperDraggable";
import { Controller, useFormContext } from "react-hook-form";
import DateComIntervalo from "../../../../../components/DateComIntervalo";
import { useEffect, useMemo, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers";
import moment, { Moment } from "moment";
import { formatarInputValorMonetário, formatarValorMonetario } from "../../../../../helpers/masks";
import { ConfigLimiteCred } from ".";
import { enqueueSnackbar } from "notistack";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../lib/axios";
import { IInputCRUDConfigLimiteCredito } from "../../../../../types/mutations/geral/cadastrar-atualizar-config-limite-credito";
import { desformatarValorMonetario } from "../../../../../helpers/unmask";
import { IDataPublicos } from "../../../../../types/api/geral/pesquisar-publicos";

export default function DialogConfiguracao({
  open,
  index,
  limite_alcada,
  handleClose,
}: DialogConfiguracaoProps) {
  const queryClient = useQueryClient();

  const {
    control,
    getFieldState,
    getValues,
    setValue,
    handleSubmit,
    watch,
  } = useFormContext();

  const [isContrato, setIsContrato] = useState("Sim");
  const listaAtt = getValues("listaAttLimiteCredito");
  const publico: IDataPublicos = watch("cliente");

  //valores importantes
  const isEdit = index !== null;

  const tipoSelectOption = [
    "Consumo",
    "Financiamento",
    "Sacaria",
    "Veterinário",
    "Fertilizantes",
    "Serviços",
    "Geral",
  ];

  const opcoesRadio = useMemo(
    () => [
      { value: "Sim", label: "Sim" },
      { value: "Não", label: "Não" },
    ],
    []
  );

  const opcoesCheckbox = useMemo(
    () => [
      "Nota promissória",
      "Hipotecária",
      "Pignoratícia",
      "Alienação fiduciária",
      "Outros",
    ],
    []
  );
  //

  const invalidateDados = () => {
    queryClient.invalidateQueries("detalhar-configuracoes-limite-credito");
    queryClient.invalidateQueries(
      `pesquisar-publicos-${publico.tipo_publico}${publico.codigo_publico}`
    );
  };

  //queries e mutations
  const { mutate: atualizarConfigLimite, isLoading: isLoadingAtt } =
    useMutation({
      mutationKey: "atualizar-configuracao-limite-credito",
      mutationFn: (inputs: IInputCRUDConfigLimiteCredito) =>
        axiosClient.post("/atualizar-configuracao-limite-credito", inputs),
      onSuccess: () => {
        enqueueSnackbar(
          "Configuração de limite de crédito atualizado com sucesso!",
          { variant: "success" }
        );
        invalidateDados();
        handleClose();
      },
    });

  const { mutate: criarConfigLimite, isLoading: isLoadingCriar } = useMutation({
    mutationKey: "cadastrar-configuracao-limite-credito",
    mutationFn: (inputs: IInputCRUDConfigLimiteCredito) =>
      axiosClient.post("/cadastrar-configuracao-limite-credito", inputs),
    onSuccess: () => {
      enqueueSnackbar("Configuração de limite de crédito criada com sucesso!", {
        variant: "success",
      });
      invalidateDados();
      handleClose();
    },
  });
  //

  //funcoes manipuladoras
  const handleChangeCheckbox = (name: string) => {
    const array = getValues(`formAttLimiteCredito.garantias`);
    const index = array.indexOf(name);

    if (index > -1) array.splice(index, 1);

    if (index === -1) array.push(name);

    setValue(`formAttLimiteCredito.garantias`, array);
  };

  const handleInsertEditForm = () => {
    let itemInsert = defaultItemAttCredito;
    if (index !== null) {
      itemInsert = getValues(`listaAttLimiteCredito[${index}]`);
    }
    setValue("formAttLimiteCredito", itemInsert);
  };

  const handleSubmitForm = async () => {
    //verificações
    const { listaAttLimiteCredito, formAttLimiteCredito, cliente } =
      getValues();

    // Validação da vigência
    const inicioMesCorrente = moment().startOf("month");
    const fimMesCorrente = moment().endOf("month");

    if (
      formAttLimiteCredito.vigencia.inicio.isBefore(inicioMesCorrente) ||
      formAttLimiteCredito.vigencia.fim.isAfter(fimMesCorrente)
    ) {
      enqueueSnackbar(
        "A vigência deve estar compreendida entre o primeiro e o último dia do mês corrente",
        { variant: "warning" }
      );
      return;
    }

    if (limite_alcada < desformatarValorMonetario(formAttLimiteCredito.limiteCredito)) {
      enqueueSnackbar(`Limite de alçada (${formatarValorMonetario(limite_alcada)}) não permite o valor informado (${formAttLimiteCredito.limiteCredito}) para alteração`, {
        variant: "error",
      });
      return;
    }

    if (!isEdit) {
      const existeTipo = listaAttLimiteCredito.some(
        (item: ConfigLimiteCred) => item.tipo === formAttLimiteCredito.tipo
      );

      if (existeTipo) {
        enqueueSnackbar("Tipo de configuração já existente", {
          variant: "error",
        });
        return;
      }
    }
    //

    const param: IInputCRUDConfigLimiteCredito = {
      tipo_publico: cliente.tipo_publico,
      codigo_publico: cliente.codigo_publico,
      tipo_limite: formAttLimiteCredito.tipo,
      limite_credito: desformatarValorMonetario(
        formAttLimiteCredito.limiteCredito
      ),
      vigencia_inicio:
        formAttLimiteCredito.vigencia.inicio.format("YYYY-MM-DD"),
      vigencia_fim: formAttLimiteCredito.vigencia.fim.format("YYYY-MM-DD"),
      tem_contrato: formAttLimiteCredito.temContrato,
      registrado: formAttLimiteCredito.registrado,
      valor_contrato: desformatarValorMonetario(
        formAttLimiteCredito.valorContrato
      ),
      vencimento_contrato: formAttLimiteCredito.vencimentoContrato
        ? formAttLimiteCredito.vencimentoContrato.format("YYYY-MM-DD")
        : null,
      garantias: formAttLimiteCredito.garantias,
    };

    if (!isEdit) await criarConfigLimite(param);
    if (isEdit) await atualizarConfigLimite(param);
  };
  //

  useEffect(() => {
    if (listaAtt) {
      setIsContrato(listaAtt[0].temContrato);
    }
    handleInsertEditForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperComponent={PaperDraggable}
      aria-labelledby="draggable-dialog-title"
      scroll="body"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle style={{ cursor: "move" }} id="draggable-dialog-title">
        {isEdit ? "Editar" : "Adicionar"} configuração
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3} my={1}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Controller
              name={`formAttLimiteCredito.tipo`}
              control={control}
              rules={{ required: "Campo obrigatório" }}
              render={({ field }) => (
                <FormControl
                  disabled
                  fullWidth
                  {...field}
                  error={getFieldState(`formAttLimiteCredito.tipo`).invalid}
                  required
                >
                  <InputLabel id="tipo-select">Tipo</InputLabel>
                  <Select
                    labelId="tipo-select"
                    value={field.value}
                    label="Tipo"
                    onChange={(e) => field.onChange(e.target.value)}
                  >
                    {tipoSelectOption.map((tipo, index) => (
                      <MenuItem key={index} value={tipo}>
                        {tipo}
                      </MenuItem>
                    ))}
                  </Select>
                  {getFieldState(`formAttLimiteCredito.tipo`).error && (
                    <FormHelperText>
                      {
                        getFieldState(`formAttLimiteCredito.tipo`).error
                          ?.message
                      }
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Controller
              name={`formAttLimiteCredito.limiteCredito`}
              control={control}
              rules={{ required: "Campo obrigatório" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Limite de crédito"
                  required
                  variant="outlined"
                  fullWidth
                  onChange={(e) =>
                    field.onChange(formatarInputValorMonetário(e.target.value))
                  }
                  error={
                    getFieldState(`formAttLimiteCredito.limiteCredito`).invalid
                  }
                  helperText={
                    getFieldState(`formAttLimiteCredito.limiteCredito`).error
                      ?.message || ""
                  }
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Controller
              name={`formAttLimiteCredito.vigencia`}
              control={control}
              render={({ field }) => (
                <DateComIntervalo
                  icon={false}
                  data={{
                    inicial: field.value?.inicio,
                    final: field.value?.fim,
                  }}
                  onChangeData={(data) => {
                    field.onChange({
                      inicio: data.inicial,
                      fim: data.final,
                    });
                  }}
                  datePickerView={["year", "month", "day"]}
                  formatString="DD/MM/YYYY"
                  labelInput="Vigência"
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <Controller
              name={`formAttLimiteCredito.temContrato`}
              control={control}
              rules={{ required: "Campo obrigatório" }}
              render={({ field }) => (
                <FormControl {...field}>
                  <FormLabel id="temContrato">Tem contrato?</FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby="temContrato"
                    defaultValue={field.value}
                    onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(value);

                      if (value === "Não") {
                        setIsContrato("Não");

                        setValue("formAttLimiteCredito.registrado", null);
                        setValue("formAttLimiteCredito.valorContrato", "");
                        setValue(
                          "formAttLimiteCredito.vencimentoContrato",
                          null
                        );
                      }
                    }}
                  >
                    {opcoesRadio.map((opcao, index) => (
                      <FormControlLabel
                        key={index}
                        value={opcao.value}
                        control={<Radio />}
                        label={opcao.label}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              )}
            />
          </Grid>

          {(watch(`formAttLimiteCredito.temContrato`) === "Sim" ||
            isContrato === "Sim") && (
              <Grid container size={{ xs: 12, md: 9 }} spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Controller
                    name={`formAttLimiteCredito.registrado`}
                    control={control}
                    rules={{
                      required:
                        watch(`formAttLimiteCredito.temContrato`) === "Sim"
                          ? "Campo obrigatório"
                          : false,
                    }}
                    render={({ field }) => (
                      <FormControl
                        fullWidth
                        {...field}
                        error={
                          getFieldState(`formAttLimiteCredito.registrado`).invalid
                        }
                        required
                      >
                        <InputLabel id="registrado-select">
                          Registrado?
                        </InputLabel>
                        <Select
                          labelId="registrado-select"
                          value={field.value}
                          label="Registrado?"
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          {opcoesRadio.map((opcao, index) => (
                            <MenuItem key={index} value={opcao.value}>
                              {opcao.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {getFieldState(`formAttLimiteCredito.registrado`)
                          .error && (
                            <FormHelperText>
                              {
                                getFieldState(`formAttLimiteCredito.registrado`)
                                  .error?.message
                              }
                            </FormHelperText>
                          )}
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Controller
                    name={`formAttLimiteCredito.valorContrato`}
                    control={control}
                    rules={{
                      required:
                        watch(`formAttLimiteCredito.temContrato`) === "Sim"
                          ? "Campo obrigatório"
                          : false,
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Valor do contrato"
                        required
                        variant="outlined"
                        fullWidth
                        onChange={(e) =>
                          field.onChange(
                            formatarInputValorMonetário(e.target.value)
                          )
                        }
                        error={
                          getFieldState(`formAttLimiteCredito.valorContrato`)
                            .invalid
                        }
                        helperText={
                          getFieldState(`formAttLimiteCredito.valorContrato`)
                            .error?.message || ""
                        }
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Controller
                    name={`formAttLimiteCredito.vencimentoContrato`}
                    control={control}
                    rules={{
                      required:
                        watch(`formAttLimiteCredito.temContrato`) === "Sim"
                          ? "Campo obrigatório"
                          : false,
                    }}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        sx={{ width: "100%" }}
                        label="Venc. contrato"
                        onChange={(data) => field.onChange(data as Moment)}
                        minDate={moment()}
                        format="DD/MM/YYYY"
                        slotProps={{
                          textField: {
                            error: getFieldState(
                              `formAttLimiteCredito.vencimentoContrato`
                            ).invalid,
                            helperText:
                              getFieldState(
                                `formAttLimiteCredito.vencimentoContrato`
                              ).error?.message || "",
                            inputProps: {
                              readOnly: true,
                            },
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            )}

          <Grid size={{ xs: 12 }}>
            <Controller
              name={`formAttLimiteCredito.garantias`}
              control={control}
              render={({ field }) => (
                <FormGroup>
                  <Typography color="#0009">Garantias</Typography>
                  <Stack direction="row" justifyContent="space-between">
                    {opcoesCheckbox.map((item) => {
                      const checkedItem = field.value?.includes(item);
                      return (
                        <FormControlLabel
                          label={item}
                          name={item}
                          checked={checkedItem}
                          control={
                            <Checkbox
                              onChange={(e) =>
                                handleChangeCheckbox(e.target.name)
                              }
                            />
                          }
                        />
                      );
                    })}
                  </Stack>
                </FormGroup>
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button
          disabled={isLoadingAtt || isLoadingCriar}
          autoFocus
          variant="contained"
          onClick={handleSubmit(handleSubmitForm)}
        >
          {(isLoadingAtt || isLoadingCriar) && (
            <CircularProgress size={16} sx={{ mr: 1 }} />
          )}
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface DialogConfiguracaoProps {
  open: boolean;
  index: number | null;
  limite_alcada: number;
  handleClose: () => void;
}

const defaultItemAttCredito = {
  tipo: "Geral",
  limiteCredito: "",
  vigencia: {
    inicio: moment(),
    fim: moment().add(1, "month"),
  },
  temContrato: "Sim",
  registrado: null,
  valorContrato: "",
  vencimentoContrato: null,
  garantias: [
    "Nota promissória",
    "Hipotecária",
    "Pignoratícia",
    "Alienação fiduciária",
    "Outros",
  ],
};
